import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import {
  Search,
  Loader2,
  X,
  Eye,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Wallet,
  Package,
  User,
  Calendar,
  AlertCircle,
  Edit3,
  FileText,
  Truck,
} from 'lucide-react'
import { apiGet, apiPut, apiPost, formatMXN } from '../lib/api'

// ---------- Tipos ----------

interface DevolucionesListItem {
  id: string
  returnNumber: string
  orderId: string
  orderNumber: string
  customerId: string | null
  customerName: string
  customerEmail: string
  orderTotal: number
  status: string
  reason: string
  refundAmount: number
  refundMethod: string | null
  createdAt: string
}

interface DevolucionesData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: DevolucionesListItem[]
}

interface DevolucionDetalle {
  id: string
  returnNumber: string
  orderId: string
  customerId: string | null
  status: string
  reason: string
  reasonDetail: string
  refundMethod: string | null
  refundAmount: number
  adminNotes: string
  customerNotes: string
  evidenceUrls: string[]
  trackingBack: string
  createdAt: string | null
  updatedAt: string | null
  order: {
    id: string
    orderNumber: string
    customerName: string
    customerEmail: string
    customerPhone: string
    total: number
    subtotal: number
    status: string
    paymentStatus: string
    paymentMethod: string
    createdAt: string | null
  } | null
  items: Array<{
    id: string
    orderItemId: string
    quantity: number
    unitRefund: number
    sku: string
    name: string
    unitPrice: number
    orderedQuantity: number
  }>
}

// ---------- Constantes ----------

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos los estados' },
  { value: 'requested', label: 'Solicitada' },
  { value: 'reviewing', label: 'En revisión' },
  { value: 'approved', label: 'Aprobada' },
  { value: 'rejected', label: 'Rechazada' },
  { value: 'shipped_back', label: 'En tránsito' },
  { value: 'received', label: 'Recibida' },
  { value: 'refunded', label: 'Reembolsada' },
  { value: 'closed', label: 'Cerrada' },
]

const STATUS_LABEL: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
)

const REASON_LABEL: Record<string, string> = {
  wrong_part: 'Pieza incorrecta',
  defective: 'Defectuosa',
  not_as_described: 'No coincide con descripción',
  changed_mind: 'Cambio de opinión',
  other: 'Otro',
}

const REFUND_METHOD_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Sin definir' },
  { value: 'original', label: 'Método original' },
  { value: 'store_credit', label: 'Saldo a favor' },
  { value: 'bank_transfer', label: 'Transferencia bancaria' },
]

const TIMELINE_STEPS = [
  { key: 'requested', label: 'Solicitada' },
  { key: 'reviewing', label: 'En revisión' },
  { key: 'approved', label: 'Aprobada' },
  { key: 'shipped_back', label: 'En tránsito' },
  { key: 'received', label: 'Recibida' },
  { key: 'refunded', label: 'Reembolsada' },
]

function statusBadge(s: string) {
  const low = (s || '').toLowerCase()
  if (low === 'refunded') return <Badge variant="success">Reembolsada</Badge>
  if (low === 'received') return <Badge variant="success">Recibida</Badge>
  if (low === 'approved') return <Badge variant="info">Aprobada</Badge>
  if (low === 'shipped_back') return <Badge variant="info">En tránsito</Badge>
  if (low === 'reviewing') return <Badge variant="warning">En revisión</Badge>
  if (low === 'requested') return <Badge variant="warning">Solicitada</Badge>
  if (low === 'rejected') return <Badge variant="danger">Rechazada</Badge>
  if (low === 'closed') return <Badge>Cerrada</Badge>
  return <Badge>{STATUS_LABEL[low] || s || 'Sin estado'}</Badge>
}

function formatDateTime(iso: string | null) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d)
  } catch {
    return iso
  }
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toISOString().slice(0, 10)
  } catch {
    return iso
  }
}

// ---------- Listado ----------

function readInitialFilters() {
  if (typeof window === 'undefined') {
    return { page: 1, q: '', status: '' }
  }
  const sp = new URLSearchParams(window.location.search)
  return {
    page: Math.max(1, parseInt(sp.get('page') || '1') || 1),
    q: sp.get('q') || '',
    status: sp.get('status') || '',
  }
}

export function Devoluciones() {
  const initial = useMemo(readInitialFilters, [])
  const [page, setPage] = useState(initial.page)
  const [qInput, setQInput] = useState(initial.q)
  const [q, setQ] = useState(initial.q)
  const [status, setStatus] = useState(initial.status)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const sp = new URLSearchParams()
    if (page > 1) sp.set('page', String(page))
    if (q) sp.set('q', q)
    if (status) sp.set('status', status)
    const qs = sp.toString()
    const newUrl = `${window.location.pathname}${qs ? '?' + qs : ''}`
    window.history.replaceState(null, '', newUrl)
  }, [page, q, status])

  const queryString = useMemo(() => {
    const sp = new URLSearchParams()
    sp.set('page', String(page))
    if (q) sp.set('q', q)
    if (status) sp.set('status', status)
    return sp.toString()
  }, [page, q, status])

  const { data, isLoading, isFetching } = useQuery<DevolucionesData>({
    queryKey: ['devoluciones', page, q, status],
    queryFn: () => apiGet(`/api/admin/devoluciones?${queryString}`),
    placeholderData: (prev) => prev,
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setQ(qInput.trim())
  }

  function clearFilters() {
    setQInput('')
    setQ('')
    setStatus('')
    setPage(1)
  }

  const hasActiveFilters = q || status

  return (
    <>
      <Topbar
        title="Devoluciones"
        subtitle={
          data
            ? `${data.total.toLocaleString('es-MX')} RMA registradas`
            : 'Cargando…'
        }
      />
      <div className="p-6">
        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-64 flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Buscar por # RMA, # pedido, cliente o email…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <X className="size-3.5" /> Limpiar
            </button>
          )}
        </form>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando devoluciones…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">RMA</th>
                      <th className="px-4 py-3 font-medium">Pedido</th>
                      <th className="px-4 py-3 font-medium">Cliente</th>
                      <th className="px-4 py-3 font-medium">Motivo</th>
                      <th className="px-4 py-3 font-medium">Fecha</th>
                      <th className="px-4 py-3 font-medium text-right">Reembolso</th>
                      <th className="px-4 py-3 font-medium">Estado</th>
                      <th className="w-12 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-12 text-center text-sm text-ink-500"
                        >
                          No se encontraron devoluciones con esos filtros.
                        </td>
                      </tr>
                    )}
                    {data.data.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => setSelectedId(r.id)}
                        className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-4 py-3 font-mono text-xs font-medium text-ink-900">
                          {r.returnNumber}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-ink-700">
                          #{r.orderNumber}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-ink-900">
                            {r.customerName || '—'}
                          </div>
                          <div className="truncate text-xs text-ink-500">
                            {r.customerEmail}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-ink-700">
                          {REASON_LABEL[r.reason] || r.reason}
                        </td>
                        <td className="px-4 py-3 text-ink-700">{formatDate(r.createdAt)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-ink-900">
                          {r.refundAmount > 0 ? formatMXN(r.refundAmount) : '—'}
                        </td>
                        <td className="px-4 py-3">{statusBadge(r.status)}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setSelectedId(r.id)}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                            title="Ver detalle"
                          >
                            <Eye className="size-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">
                    {data.pages.toLocaleString('es-MX')}
                  </strong>{' '}
                  ({data.total.toLocaleString('es-MX')} devoluciones)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">
                    {page}
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedId && (
        <DevolucionDetalleModal
          id={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </>
  )
}

// ---------- Detalle (modal) ----------

function Card({
  title,
  icon,
  children,
  actions,
}: {
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
  actions?: React.ReactNode
}) {
  return (
    <section className="rounded-2xl border border-ink-200 bg-white">
      <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          {icon}
          {title}
        </div>
        {actions}
      </header>
      <div className="p-5">{children}</div>
    </section>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="text-ink-500">{label}</span>
      <span className="text-right font-medium text-ink-900">{value}</span>
    </div>
  )
}

function DevolucionDetalleModal({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery<{ data: DevolucionDetalle }>({
    queryKey: ['devolucion', id],
    queryFn: () => apiGet(`/api/admin/devoluciones/${id}`),
  })

  const r = data?.data

  // Estado local del formulario — se inicializa cuando llega la data.
  const [status, setStatus] = useState<string>('')
  const [refundMethod, setRefundMethod] = useState<string>('')
  const [refundAmount, setRefundAmount] = useState<string>('')
  const [adminNotes, setAdminNotes] = useState<string>('')
  const [trackingBack, setTrackingBack] = useState<string>('')

  useEffect(() => {
    if (r) {
      setStatus(r.status)
      setRefundMethod(r.refundMethod || '')
      setRefundAmount(String(r.refundAmount || 0))
      setAdminNotes(r.adminNotes || '')
      setTrackingBack(r.trackingBack || '')
    }
  }, [r])

  const updateMut = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiPut<{ data: DevolucionDetalle }>(`/api/admin/devoluciones/${id}`, payload),
    onSuccess: (res) => {
      queryClient.setQueryData(['devolucion', id], res)
      queryClient.invalidateQueries({ queryKey: ['devoluciones'] })
      setError(null)
      setSuccess('Devolución actualizada')
      setTimeout(() => setSuccess(null), 2500)
    },
    onError: (e: any) => {
      setSuccess(null)
      setError(e?.message || 'Error al actualizar')
    },
  })

  const approveMut = useMutation({
    mutationFn: () => apiPost<{ data: any }>(`/api/admin/devoluciones/${id}/approve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devolucion', id] })
      queryClient.invalidateQueries({ queryKey: ['devoluciones'] })
      setSuccess('Devolución aprobada')
      setTimeout(() => setSuccess(null), 2500)
    },
    onError: (e: any) => setError(e?.message || 'Error al aprobar'),
  })

  const rejectMut = useMutation({
    mutationFn: (reason: string) =>
      apiPost<{ data: any }>(`/api/admin/devoluciones/${id}/reject`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devolucion', id] })
      queryClient.invalidateQueries({ queryKey: ['devoluciones'] })
      setSuccess('Devolución rechazada')
      setTimeout(() => setSuccess(null), 2500)
    },
    onError: (e: any) => setError(e?.message || 'Error al rechazar'),
  })

  const refundMut = useMutation({
    mutationFn: () =>
      apiPost<{ data: any }>(`/api/admin/devoluciones/${id}/refund`, {
        refundAmount: parseFloat(refundAmount || '0') || 0,
        refundMethod: refundMethod || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devolucion', id] })
      queryClient.invalidateQueries({ queryKey: ['devoluciones'] })
      setSuccess('Marcada como reembolsada')
      setTimeout(() => setSuccess(null), 2500)
    },
    onError: (e: any) => setError(e?.message || 'Error al marcar reembolso'),
  })

  const timeline = useMemo(() => {
    if (!r) return []
    const cancelled = r.status === 'rejected' || r.status === 'closed'
    const currentIdx = TIMELINE_STEPS.findIndex((s) => s.key === r.status)
    return TIMELINE_STEPS.map((s, idx) => ({
      ...s,
      done: !cancelled && currentIdx >= 0 && idx < currentIdx,
      current: !cancelled && idx === currentIdx,
    }))
  }, [r])

  function handleSave() {
    const payload: Record<string, unknown> = {}
    if (status !== r?.status) payload.status = status
    if ((refundMethod || null) !== (r?.refundMethod ?? null)) {
      payload.refundMethod = refundMethod || null
    }
    const newAmount = parseFloat(refundAmount || '0') || 0
    if (newAmount !== r?.refundAmount) payload.refundAmount = newAmount
    if (adminNotes !== (r?.adminNotes || '')) payload.adminNotes = adminNotes
    if (trackingBack !== (r?.trackingBack || '')) payload.trackingBack = trackingBack
    if (Object.keys(payload).length === 0) {
      setError('No hay cambios para guardar')
      return
    }
    updateMut.mutate(payload)
  }

  function handleReject() {
    const reason = window.prompt('Motivo del rechazo (opcional):') || ''
    rejectMut.mutate(reason)
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-950/60 p-4">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-ink-50 shadow-xl">
        <header className="flex items-center justify-between border-b border-ink-200 bg-white px-5 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
            >
              <ArrowLeft className="size-4" />
            </button>
            <div>
              <h2 className="text-base font-semibold text-ink-900">
                Devolución{' '}
                <span className="font-mono">{r?.returnNumber || '—'}</span>
              </h2>
              <div className="text-xs text-ink-500">
                Creada {formatDateTime(r?.createdAt || null)}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="grid place-items-center p-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando devolución…</p>
            </div>
          )}
          {(isError || !r) && !isLoading && (
            <div className="p-6">
              <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
                <AlertCircle className="mx-auto size-8 text-red-500" />
                <p className="mt-3 text-sm text-red-700">
                  No se pudo cargar la devolución.
                </p>
              </div>
            </div>
          )}

          {r && (
            <div className="space-y-4 p-6">
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                  <AlertCircle className="size-4" /> {error}
                </div>
              )}
              {success && (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
                  <CheckCircle2 className="size-4" /> {success}
                </div>
              )}

              {/* Acciones rápidas */}
              <div className="flex flex-wrap gap-2">
                <button
                  disabled={
                    approveMut.isPending ||
                    r.status === 'approved' ||
                    r.status === 'refunded' ||
                    r.status === 'rejected'
                  }
                  onClick={() => approveMut.mutate()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                >
                  <CheckCircle2 className="size-3.5" /> Aprobar
                </button>
                <button
                  disabled={
                    rejectMut.isPending ||
                    r.status === 'rejected' ||
                    r.status === 'refunded'
                  }
                  onClick={handleReject}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
                >
                  <XCircle className="size-3.5" /> Rechazar
                </button>
                <button
                  disabled={refundMut.isPending || r.status === 'refunded'}
                  onClick={() => refundMut.mutate()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                >
                  <Wallet className="size-3.5" /> Marcar reembolsado
                </button>
              </div>

              <div className="grid gap-4 lg:grid-cols-3">
                {/* Resumen */}
                <Card title="Resumen" icon={<FileText className="size-4 text-brand-600" />}>
                  <div className="mb-3 flex items-center gap-2">
                    {statusBadge(r.status)}
                  </div>
                  <Row label="# RMA" value={<span className="font-mono">{r.returnNumber}</span>} />
                  <Row label="Motivo" value={REASON_LABEL[r.reason] || r.reason} />
                  {r.reasonDetail && (
                    <div className="mt-2 rounded-lg bg-ink-50 p-3 text-xs text-ink-700">
                      <div className="mb-1 font-semibold text-ink-600">Detalle</div>
                      <p className="whitespace-pre-wrap">{r.reasonDetail}</p>
                    </div>
                  )}
                  <hr className="my-3 border-ink-100" />
                  <Row
                    label="Monto reembolso"
                    value={
                      r.refundAmount > 0 ? formatMXN(r.refundAmount) : '—'
                    }
                  />
                  <Row
                    label="Método"
                    value={
                      r.refundMethod
                        ? REFUND_METHOD_OPTIONS.find((m) => m.value === r.refundMethod)
                            ?.label || r.refundMethod
                        : '—'
                    }
                  />
                </Card>

                {/* Pedido */}
                <Card title="Pedido" icon={<Package className="size-4 text-brand-600" />}>
                  {r.order ? (
                    <>
                      <Row
                        label="# Pedido"
                        value={
                          <span className="font-mono">#{r.order.orderNumber}</span>
                        }
                      />
                      <Row label="Total pedido" value={formatMXN(r.order.total)} />
                      <Row
                        label="Estado pedido"
                        value={<span className="capitalize">{r.order.status}</span>}
                      />
                      <Row
                        label="Pago"
                        value={
                          <span className="capitalize">
                            {r.order.paymentMethod || '—'} ({r.order.paymentStatus})
                          </span>
                        }
                      />
                      <Row
                        label="Fecha pedido"
                        value={formatDateTime(r.order.createdAt)}
                      />
                    </>
                  ) : (
                    <p className="text-sm text-ink-500">Sin datos del pedido</p>
                  )}
                </Card>

                {/* Cliente */}
                <Card title="Cliente" icon={<User className="size-4 text-brand-600" />}>
                  {r.order ? (
                    <>
                      <Row label="Nombre" value={r.order.customerName || '—'} />
                      <Row
                        label="Email"
                        value={
                          <span className="break-all">{r.order.customerEmail || '—'}</span>
                        }
                      />
                      <Row label="Teléfono" value={r.order.customerPhone || '—'} />
                      {r.customerNotes && (
                        <div className="mt-3 rounded-lg bg-ink-50 p-3 text-xs">
                          <div className="mb-1 font-semibold text-ink-600">
                            Comentario del cliente
                          </div>
                          <p className="whitespace-pre-wrap text-ink-700">
                            {r.customerNotes}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-ink-500">Sin datos del cliente</p>
                  )}
                </Card>
              </div>

              {/* Items */}
              <Card
                title={`Items a devolver (${r.items.length})`}
                icon={<Package className="size-4 text-brand-600" />}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-ink-50">
                      <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                        <th className="px-4 py-2 font-medium">SKU</th>
                        <th className="px-4 py-2 font-medium">Producto</th>
                        <th className="px-4 py-2 font-medium text-right">P. unitario</th>
                        <th className="px-4 py-2 font-medium text-right">Cant.</th>
                        <th className="px-4 py-2 font-medium text-right">Reembolso u.</th>
                        <th className="px-4 py-2 font-medium text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.items.length === 0 && (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-6 text-center text-sm text-ink-500"
                          >
                            Sin items
                          </td>
                        </tr>
                      )}
                      {r.items.map((it) => {
                        const unit = it.unitRefund > 0 ? it.unitRefund : it.unitPrice
                        return (
                          <tr key={it.id} className="border-t border-ink-100">
                            <td className="px-4 py-3 font-mono text-xs text-ink-700">
                              {it.sku || '—'}
                            </td>
                            <td className="px-4 py-3 text-ink-900">{it.name || '—'}</td>
                            <td className="px-4 py-3 text-right text-ink-700">
                              {formatMXN(it.unitPrice)}
                            </td>
                            <td className="px-4 py-3 text-right text-ink-700">
                              {it.quantity} / {it.orderedQuantity}
                            </td>
                            <td className="px-4 py-3 text-right text-ink-700">
                              {it.unitRefund > 0 ? formatMXN(it.unitRefund) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-ink-900">
                              {formatMXN(unit * it.quantity)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Evidencia */}
              {r.evidenceUrls.length > 0 && (
                <Card
                  title="Evidencia"
                  icon={<FileText className="size-4 text-brand-600" />}
                >
                  <div className="flex flex-wrap gap-3">
                    {r.evidenceUrls.map((url, i) => (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative block size-24 overflow-hidden rounded-lg border border-ink-200 bg-ink-50"
                      >
                        <img
                          src={url}
                          alt={`Evidencia ${i + 1}`}
                          className="size-full object-cover transition group-hover:scale-105"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none'
                          }}
                        />
                      </a>
                    ))}
                  </div>
                </Card>
              )}

              <div className="grid gap-4 lg:grid-cols-2">
                {/* Edición */}
                <Card
                  title="Datos administrativos"
                  icon={<Edit3 className="size-4 text-brand-600" />}
                >
                  <div className="space-y-3">
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-ink-700">
                        Estado
                      </span>
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      >
                        {STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="mb-1 block text-xs font-medium text-ink-700">
                          Monto reembolso
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={refundAmount}
                          onChange={(e) => setRefundAmount(e.target.value)}
                          className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-medium text-ink-700">
                          Método
                        </span>
                        <select
                          value={refundMethod}
                          onChange={(e) => setRefundMethod(e.target.value)}
                          className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                        >
                          {REFUND_METHOD_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-ink-700">
                        Tracking de retorno
                      </span>
                      <input
                        type="text"
                        value={trackingBack}
                        onChange={(e) => setTrackingBack(e.target.value)}
                        placeholder="Número de guía…"
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-ink-700">
                        Notas internas
                      </span>
                      <textarea
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        rows={4}
                        placeholder="Notas visibles solo para el equipo…"
                        className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                      />
                    </label>
                    <div className="flex justify-end">
                      <button
                        disabled={updateMut.isPending}
                        onClick={handleSave}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                      >
                        {updateMut.isPending && (
                          <Loader2 className="size-3.5 animate-spin" />
                        )}
                        Guardar cambios
                      </button>
                    </div>
                  </div>
                </Card>

                {/* Timeline */}
                <Card
                  title="Línea de tiempo"
                  icon={<Calendar className="size-4 text-brand-600" />}
                >
                  <ol className="relative space-y-4">
                    {timeline.map((step, idx) => (
                      <li key={step.key} className="relative flex gap-3 pl-1">
                        <div
                          className={`mt-0.5 size-4 shrink-0 rounded-full border-2 ${
                            step.done
                              ? 'border-brand-600 bg-brand-600'
                              : step.current
                                ? 'border-brand-600 bg-white'
                                : 'border-ink-200 bg-white'
                          }`}
                        />
                        {idx < timeline.length - 1 && (
                          <div
                            className={`absolute left-2 top-5 h-full w-px ${
                              step.done ? 'bg-brand-600' : 'bg-ink-200'
                            }`}
                          />
                        )}
                        <div className="flex-1 pb-3">
                          <div
                            className={`text-sm font-medium ${
                              step.done || step.current ? 'text-ink-900' : 'text-ink-500'
                            }`}
                          >
                            {step.label}
                          </div>
                        </div>
                      </li>
                    ))}
                    {(r.status === 'rejected' || r.status === 'closed') && (
                      <li className="flex gap-3 pl-1">
                        <div className="mt-0.5 size-4 shrink-0 rounded-full border-2 border-red-500 bg-red-500" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-red-700">
                            {STATUS_LABEL[r.status]}
                          </div>
                          <div className="text-xs text-ink-500">
                            {formatDateTime(r.updatedAt)}
                          </div>
                        </div>
                      </li>
                    )}
                  </ol>
                  {r.trackingBack && (
                    <div className="mt-4 flex items-center gap-2 rounded-lg bg-ink-50 p-3 text-xs text-ink-700">
                      <Truck className="size-3.5 text-ink-500" />
                      Guía de retorno:{' '}
                      <span className="font-mono">{r.trackingBack}</span>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
